import { useState, useEffect, type ChangeEvent, type DragEvent } from "react";
import { X, Upload, ImageIcon, Trash2 } from "lucide-react";
import { useEditorStore } from "../store";

export interface MediaAsset {
  id: string;
  url: string;
  filename: string;
  sizeBytes: number;
  isCustom?: boolean;
}

const STOCK_ASSETS: MediaAsset[] = [
  {
    id: "stock-1",
    url: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80",
    filename: "modern-office.jpg",
    sizeBytes: 450000,
    isCustom: false,
  },
  {
    id: "stock-2",
    url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80",
    filename: "skyscraper-architecture.jpg",
    sizeBytes: 620000,
    isCustom: false,
  },
  {
    id: "stock-3",
    url: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80",
    filename: "team-collaboration.jpg",
    sizeBytes: 510000,
    isCustom: false,
  },
];

const LOCAL_STORAGE_KEY = "zirro_media_library";

function getStoredCustomAssets(): MediaAsset[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn("Failed to parse media library from localStorage", err);
    return [];
  }
}

export function MediaLibraryModal() {
  const isOpen = useEditorStore((s) => s.isMediaModalOpen);
  const closeModal = useEditorStore((s) => s.closeMediaModal);
  const onSelect = useEditorStore((s) => s.onSelectMediaCallback);
  const uploadImage = useEditorStore((s) => s.uploadImage);
  const [uploading, setUploading] = useState(false);

  const [customAssets, setCustomAssets] = useState<MediaAsset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCustomAssets(getStoredCustomAssets());
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Uploads go to the site's media storage when the app provides an uploader
  // (see EditorLayout onUploadImage), so pages keep a short URL; the library
  // remembers them in this browser.
  const processFile = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const url = await uploadImage(file);
      const newAsset: MediaAsset = {
        id: `custom-media-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        url,
        filename: file.name,
        sizeBytes: file.size,
        isCustom: true,
      };

      const updated = [newAsset, ...customAssets];
      setCustomAssets(updated);
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
      } catch (err) {
        console.warn("Failed to persist uploaded asset to localStorage", err);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void processFile(file);
    }
    // Reset file input value
    e.target.value = "";
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void processFile(file);
    }
  };

  const handleDeleteCustomAsset = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = customAssets.filter((a) => a.id !== id);
    setCustomAssets(updated);
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
    } catch (err) {
      console.warn("Failed to update localStorage after deleting asset", err);
    }
  };

  const handleSelect = (url: string) => {
    onSelect?.(url);
    closeModal();
  };

  const allAssets = [...customAssets, ...STOCK_ASSETS];

  return (
    <div className="zirro-modal-backdrop" onClick={closeModal}>
      <div
        className="zirro-modal-dialog zirro-media-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="zirro-modal-header">
          <h3>Media Library</h3>
          <button
            type="button"
            className="zirro-modal-close"
            onClick={closeModal}
            aria-label="Close media library"
          >
            <X size={16} />
          </button>
        </div>

        <div className="zirro-modal-body">
          <div
            className={`zirro-upload-dropzone ${isDragging ? "zirro-upload-dropzone--dragging" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <label className="zirro-upload-label">
              <Upload size={24} style={{ color: "var(--ed-accent)", marginBottom: 4 }} />
              <span>
                {uploading
                  ? "Uploading…"
                  : isDragging
                    ? "Drop image here to upload..."
                    : "Click or drag image to upload (JPEG, PNG, WebP, GIF — Max 5MB)"}
              </span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileInputChange}
              />
            </label>
            {error ? <p className="zirro-error-text">{error}</p> : null}
          </div>

          {allAssets.length === 0 ? (
            <div className="zirro-inspector-empty" style={{ padding: "32px 16px" }}>
              <ImageIcon size={32} style={{ color: "var(--ed-text-dim)" }} />
              <p>No images yet. Upload one above.</p>
            </div>
          ) : (
            <div className="zirro-media-grid">
              {allAssets.map((asset) => (
                <div
                  key={asset.id}
                  className="zirro-media-card"
                  onClick={() => handleSelect(asset.url)}
                  title={asset.filename}
                >
                  <img src={asset.url} alt={asset.filename} />
                  {asset.isCustom ? (
                    <button
                      type="button"
                      className="zirro-media-card-delete"
                      onClick={(e) => handleDeleteCustomAsset(asset.id, e)}
                      title="Delete uploaded image"
                      aria-label="Delete image"
                    >
                      <Trash2 size={12} />
                    </button>
                  ) : null}
                  <div className="zirro-media-card-info">
                    <span className="zirro-media-name">{asset.filename}</span>
                    <span className="zirro-media-size">
                      {(asset.sizeBytes / 1024).toFixed(0)} KB
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
